import { assume, merge } from "./utils";
import * as JSON5 from "json5";
import { PackSource } from "./pack_sources";

export interface AssetPacks {
    [id: string]: AssetPackDescription;
}

export interface AssetPackDescription {
    url: string;
    displayName: string;
}

export interface AssetPackMeta {
    license: string
}

export interface ColorDescription {
    displayName: string,
    default: string
}

export interface Part {
    type: "composite";
    components: Component[];
    displayName: string;
    color: string;
    layer: string;
}
export interface Component {
    url: string;
    color: string;
    layer: string;
}

export interface AssetType {
    displayName: string,
    color: string,
    layer: string,
    items: {
        [id: string]: Part
    }
}

export interface OptimizedImageVariants {
    [type: string]: OptimizedImageVariant
};

export interface OptimizedImageVariant {
    url: string,
    offset: { x: number, y: number },
    size: { w: number, h: number }
};

export class AssetPack {
    meta: AssetPackMeta
    render: {
        canvasWidth: number,
        canvasHeight: number
    }
    layers: {
        [id: string]: {}
    }
    colors: {
        [id: string]: ColorDescription
    }
    assets: {
        [group: string]: AssetType
    }
    optimizedImages: {
        [url: string]: OptimizedImageVariants
    }
    constructor() {
        this.render = { canvasWidth: -1, canvasHeight: -1 };
        this.layers = {};
        this.colors = {};
        this.assets = {};
    }
};

function normalizePack(dirtyPack: any): AssetPack {
    const pack = new AssetPack();
    pack.render = dirtyPack.render;
    pack.layers = dirtyPack.layers;

    // meta
    pack.meta = {
        license: dirtyPack.meta?.license || "ARR.\nArt and adaptation by Anonymous.",
    };

    // colors 
    for (const colorId in dirtyPack.colors) {
        const color = dirtyPack.colors[colorId];
        if (!color.default)
            color.default = "#ffffff";
    }
    pack.colors = dirtyPack.colors;

    // optimized image variants
    pack.optimizedImages = {};
    if ("optimizedImages" in dirtyPack)
        for (const imageUrl in dirtyPack.optimizedImages) {
            pack.optimizedImages[imageUrl] = {};
            for (const variantType in dirtyPack.optimizedImages[imageUrl]) {
                const dirtyVariant = dirtyPack.optimizedImages[imageUrl][variantType];

                pack.optimizedImages[imageUrl][variantType] = {
                    url: dirtyVariant.url,
                    offset: {
                        x: dirtyVariant.offset.x,
                        y: dirtyVariant.offset.y,
                    },
                    size: {
                        w: dirtyVariant.size.w,
                        h: dirtyVariant.size.h,
                    }
                };
            }
        }

    // assets
    for (const typeName in dirtyPack.assets) {
        const dirtyType = dirtyPack.assets[typeName];
        const type: AssetType =
        {
            displayName: dirtyType.displayName,
            color: dirtyType.color,
            layer: dirtyType.layer,
            items: {}
        }

        for (const itemName in dirtyType.items) {
            const dirtyItem = dirtyType.items[itemName];
            if (!dirtyItem.type) dirtyItem.type = "image";
            if (!dirtyItem.layer) dirtyItem.layer = dirtyType.layer;
            if (!dirtyItem.color) dirtyItem.color = dirtyType.color;
            if (!dirtyItem.displayName) dirtyItem.displayName = itemName;

            if (dirtyItem.components) {
                for (const dirtyComp of dirtyItem.components) {
                    if (!dirtyComp.layer) dirtyComp.layer = dirtyItem.layer;
                    if (!dirtyComp.color) dirtyComp.color = dirtyItem.color;

                    if (!dirtyComp.url) dirtyComp.url = dirtyItem.url;
                }
            }
            else {
                dirtyItem.components = [
                    {
                        layer: dirtyItem.layer,
                        color: dirtyItem.color,
                        url: dirtyItem.url
                    }
                ]
            }
            type.items[itemName] = dirtyItem;
        }
        pack.assets[typeName] = type;
    }
    return pack;
}

async function loadFile(packSource: PackSource, name: string) {
    const file = JSON5.parse(await packSource.loadText(name));
    if ("include" in file) {
        const includedFileNames: string[] = file.include;
        const includedFiles = await Promise.all(includedFileNames.map(x => loadFile(packSource, x)));
        for (const includedFile of includedFiles) {
            merge(includedFile, file);
        }
    }
    return file;
}

export async function loadFrom(packSource: PackSource) {
    const assets = await loadFile(packSource, "main.json5");
    return normalizePack(assets);
}